import { API, unwrapData } from '../axios-instance'
import { SetMediaObjectParams } from '../media'
import { createBatchRoutes } from '../shared/batch'
import { PageResult } from '../shared/types'
import {
  BatchAddToTeamsSearchParams,
  BatchResendWelcomeEmailParams,
  BatchSuspendTenantMembersParams,
  BatchUnsuspendTenantMembersParams,
  CreateTenantMemberParams,
  DeleteTenantMembersParams,
  DeleteTenantMembersResult,
  EditLocaleParams,
  ExportOfflineCodesParams,
  GetTenantMemberParams,
  ListTenantMembersParams,
  Locale,
  TenantMember,
  TenantMemberAvatar,
  TenantMembersActivationStats,
  TenantMembersCount,
  TenantMembersCountParams,
  UpdateTenantMemberParams,
  UpdateTenantMemberRoleParams,
} from './types'

export const listTenantMembers = async (params: ListTenantMembersParams) => {
  return await API.get<PageResult<TenantMember>>('/identity/tenantMembers', {
    params,
  }).then(unwrapData)
}

export const editTenantMemberLocale = async (params: EditLocaleParams) => {
  return await API.post<Locale>('/identity/tenantMembers/locale', params).then(unwrapData)
}

export const getTenantMemberLocale = async () => {
  return await API.get<Locale>('/identity/tenantMembers/locale').then(unwrapData)
}

export const getTenantMember = async (id: string, params: GetTenantMemberParams) => {
  return await API.get<TenantMember>(`/identity/tenantMembers/${id}`, { params }).then(unwrapData)
}

/**
 * Note: This only sets a browser-detected default value, do not use this to update
 * a member's preference.
 * */
export const updateTenantMemberDefaultTimezone = async (params: { timezone: string }) => {
  return await API.patch<Locale>('/identity/tenantMembers/timezone', params).then(unwrapData)
}

export const updateTenantMember = async (id: string, params: UpdateTenantMemberParams) => {
  return await API.patch<TenantMember>(`/identity/tenantMembers/${id}`, params).then(unwrapData)
}

export const updateTenantMemberRole = async (id: string, params: UpdateTenantMemberRoleParams) => {
  return await API.post<TenantMember>(`/identity/tenantMembers/${id}/role`, params).then(unwrapData)
}

export const createTenantMember = async (params: CreateTenantMemberParams) => {
  return await API.post<TenantMember>('/identity/tenantMembers', params).then(unwrapData)
}

export const suspendTenantMember = async (id: string) => {
  return await API.post<TenantMember>(`/identity/tenantMembers/${id}/suspend`).then(unwrapData)
}

export const batchSuspendTenantMembers = createBatchRoutes<
  BatchSuspendTenantMembersParams,
  TenantMember,
  unknown,
  unknown
>('/identity/tenantMembers/batchSuspend')

export const unsuspendTenantMember = async (id: string) => {
  return await API.post<TenantMember>(`/identity/tenantMembers/${id}/unsuspend`).then(unwrapData)
}

export const batchUnsuspendTenantMembers = createBatchRoutes<
  BatchUnsuspendTenantMembersParams,
  TenantMember,
  unknown,
  unknown
>('/identity/tenantMembers/batchUnsuspend')

export const batchAddMembersToTeams = createBatchRoutes<
  BatchAddToTeamsSearchParams,
  TenantMember,
  unknown,
  unknown
>('/identity/tenantMembers/batchAddToTeams')

export const setMemberAvatar = async (id: string, params: SetMediaObjectParams) => {
  return await API.post<TenantMemberAvatar>(`/identity/tenantMembers/${id}/avatar`, params).then(
    unwrapData,
  )
}

export const getTenantMembersCount = async (params?: TenantMembersCountParams) => {
  // NOTE: POST because teamIdIn could exceed the max URL size limit
  return await API.post<TenantMembersCount>('/identity/tenantMembers/count', params).then(
    unwrapData,
  )
}

export const listTenantManagers = async (params: ListTenantMembersParams) => {
  return await API.get<PageResult<TenantMember>>('/identity/tenantMembers/managers', {
    params,
  }).then(unwrapData)
}

export const batchResendWelcomeEmail = createBatchRoutes<
  BatchResendWelcomeEmailParams,
  TenantMember,
  unknown,
  unknown
>('/identity/tenantMembers/batchWelcome')

export const resendWelcomeTenantMember = async (id: string) => {
  return await API.post<TenantMember>(`/identity/tenantMembers/${id}/welcome`).then(unwrapData)
}

export const exportMembersCsvRecords = async (params: ListTenantMembersParams) => {
  return await API.post<BlobPart>('/identity/tenantMembers/export', params, {
    params: { sorting: params.sorting },
  }).then(unwrapData)
}

export const exportTenantMembersOfflineCodesCsv = async (params: ExportOfflineCodesParams) => {
  return await API.post<BlobPart>('/identity/tenantMembers/offlineCodes', params).then(unwrapData)
}

export const getTenantMembersActivationStats = async () => {
  return await API.get<TenantMembersActivationStats>('/identity/tenantMembers/summary').then(
    unwrapData,
  )
}

export const batchDeleteTenantMembers = async (params: DeleteTenantMembersParams) => {
  return await API.delete<DeleteTenantMembersResult>('/identity/tenantMembers', {
    data: params,
  }).then(unwrapData)
}
